/**
 * EMZI NEXUS V6.4 - Compact Language Toggle with Globe Icon
 * Minimalist popover menu: BM · Mix · EN
 */

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

const LanguageToggle = ({ placement = 'bottom-end' }) => {
  const { language, changeLanguage, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const items = [
    { key: 'bm', label: 'Bahasa Melayu', hint: 'Santai' },
    { key: 'urban', label: 'Urban', hint: 'BM + EN terms' },
    { key: 'en', label: 'English', hint: '' },
  ];

  const selectLang = async (value) => {
    changeLanguage(value);
    localStorage.setItem('emzi.lang', value);
    
    // Persist to server
    try {
      const token = secureStorage.getItem('nexus_access_token');
            
      // Map lowercase to uppercase for backend
      const backendLang = value === 'bm' ? 'BM' : value === 'en' ? 'EN' : 'Urban';
      
      await fetch(`${BACKEND_URL}/api/v1/users/me/preferences?user_id=demo_executor`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ preferred_language: backendLang })
      });
    } catch (err) {
      console.warn('[LanguageToggle] Failed to persist to server:', err);
    }
    
    // Dispatch event for re-renders
    window.dispatchEvent(new Event('LanguageChanged'));
    setOpen(false);
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    
    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open]);

  return (
    <div 
      ref={ref} 
      style={{ position: 'relative' }}
      className="lang-toggle"
    >
      <button
        aria-label={t('common.language') || 'Language'}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.backgroundColor = 'transparent';
        }}
        style={{
          padding: '10px',
          borderRadius: '8px',
          background: open ? 'rgba(255,255,255,0.15)' : 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '44px',
          height: '44px',
          color: '#FFFFFF',
          transition: 'all 0.2s',
          outline: 'none'
        }}
        onFocus={(e) => {
          e.currentTarget.style.outline = '2px solid rgba(255,255,255,0.5)';
          e.currentTarget.style.outlineOffset = '2px';
        }}
        onBlur={(e) => {
          e.currentTarget.style.outline = 'none';
        }}
      >
        {/* Globe Icon */}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      </button>

      {open && (
        <div 
          role="menu"
          style={{
            position: 'absolute',
            right: placement.includes('end') ? 0 : 'auto',
            left: placement.includes('start') ? 0 : 'auto',
            top: 'calc(100% + 8px)',
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.08)',
            padding: '6px',
            minWidth: '200px',
            zIndex: 1000,
            animation: 'fadeIn 0.15s ease-out'
          }}
        >
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-4px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          
          {items.map(it => (
            <button
              key={it.key}
              role="menuitemradio"
              aria-checked={language === it.key}
              onClick={() => selectLang(it.key)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#F5F8FF';
              }}
              onMouseLeave={(e) => {
                if (language !== it.key) e.currentTarget.style.background = 'transparent';
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: 'none',
                background: language === it.key ? '#F5F8FF' : 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: language === it.key ? 600 : 500,
                color: '#1F2937',
                transition: 'all 0.15s',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.outline = '2px solid #0e67a2';
                e.currentTarget.style.outlineOffset = '-2px';
              }}
              onBlur={(e) => {
                e.currentTarget.style.outline = 'none';
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{it.label}</span>
                {it.hint && (
                  <span style={{ 
                    fontSize: '11px', 
                    opacity: 0.65, 
                    marginTop: '2px',
                    color: '#6B7280'
                  }}>
                    {it.hint}
                  </span>
                )}
              </div>
              {language === it.key && (
                <span style={{ 
                  color: '#0e67a2', 
                  fontWeight: 700,
                  fontSize: '16px',
                  marginLeft: '12px'
                }}>
                  ✓
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageToggle;
